/**
 * PlatformSettingsTab — Platform Settings with sidebar category navigation and company scope.
 */
import { useState, useCallback, useMemo, useEffect } from "react";
import { useSearchParams } from "react-router";
import { CompanyScopeSelector } from "./components/company-scope-selector";
import { CategorySettingsPanel } from "./components/category-settings-panel";
import { useSettings, useUpdateCategorySettings } from "@/hooks/api/use-settings";
import { CATEGORY_CONFIG, CATEGORY_ORDER } from "./category-config";
import { toast } from "@/lib/toast-service";
import type { SettingCategory } from "@/types/api/settings";
import { cx } from "@/utils/cx";

function normalizeUrlSearchParams(params: URLSearchParams): URLSearchParams | null {
    const p = new URLSearchParams(params);
    let changed = false;

    if (p.has("cards")) {
        const first = p
            .get("cards")!
            .split(",")
            .find((x): x is SettingCategory => CATEGORY_ORDER.includes(x as SettingCategory));
        p.delete("cards");
        if (first && !p.get("category")) {
            p.set("category", first);
        }
        changed = true;
    }
    const rawCat = p.get("category");
    if (!rawCat) {
        p.set("category", "scheduler");
        changed = true;
    } else if (!CATEGORY_ORDER.includes(rawCat as SettingCategory)) {
        p.set("category", "scheduler");
        changed = true;
    }

    return changed ? p : null;
}

function selectedCategoryFromParams(params: URLSearchParams): SettingCategory {
    const c = params.get("category");
    if (c && CATEGORY_ORDER.includes(c as SettingCategory)) {
        return c as SettingCategory;
    }
    const cards = params.get("cards");
    if (cards) {
        const first = cards
            .split(",")
            .find((x): x is SettingCategory => CATEGORY_ORDER.includes(x as SettingCategory));
        if (first) return first;
    }
    return "scheduler";
}

export function PlatformSettingsTab() {
    const [searchParams, setSearchParams] = useSearchParams();
    const [selectedScope, setSelectedScope] = useState("global");
    const [savingCategory, setSavingCategory] = useState<SettingCategory | null>(null);

    const { data: settingsData, isLoading, error, refetch } = useSettings(selectedScope);
    const updateCategorySettings = useUpdateCategorySettings();

    useEffect(() => {
        const next = normalizeUrlSearchParams(searchParams);
        if (next) {
            setSearchParams(next, { replace: true });
        }
    }, [searchParams, setSearchParams]);

    const selectedCategory = useMemo(
        () => selectedCategoryFromParams(searchParams),
        [searchParams]
    );

    const handleSelectCategory = useCallback(
        (category: SettingCategory) => {
            const p = new URLSearchParams(searchParams);
            p.set("category", category);
            setSearchParams(p, { replace: true });
        },
        [searchParams, setSearchParams]
    );

    const handleSaveCategory = useCallback(
        async (category: SettingCategory, values: Record<string, unknown>) => {
            setSavingCategory(category);
            try {
                await updateCategorySettings.mutateAsync({
                    category,
                    settings: values,
                    companyId: selectedScope !== "global" ? selectedScope : undefined,
                });
                toast.success(`${category} settings saved successfully`);
            } catch (err) {
                console.error("Failed to save settings:", err);
                toast.error(`Failed to save ${category} settings`);
            } finally {
                setSavingCategory(null);
            }
        },
        [updateCategorySettings, selectedScope]
    );

    const handleResetCategory = useCallback(() => {
        refetch();
    }, [refetch]);

    const categorizedSettings = useMemo(() => {
        if (!settingsData) return {};
        return settingsData;
    }, [settingsData]);

    if (isLoading) {
        return (
            <div className="space-y-6">
                <CompanyScopeSelector selectedScope={selectedScope} onScopeChange={setSelectedScope} />
                <div className="flex items-center justify-center py-12 text-tertiary">Loading settings...</div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="space-y-6">
                <CompanyScopeSelector selectedScope={selectedScope} onScopeChange={setSelectedScope} />
                <div className="rounded-lg border border-error-primary/20 bg-error-primary/5 p-6">
                    <p className="text-error-primary font-medium">Failed to load settings</p>
                    <p className="text-sm text-tertiary mt-1">Please try refreshing the page or contact support.</p>
                </div>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            <CompanyScopeSelector selectedScope={selectedScope} onScopeChange={setSelectedScope} />

            <div className="grid grid-cols-1 gap-6 lg:grid-cols-[240px_1fr] lg:items-start">
                <nav
                    className="flex flex-row gap-1 overflow-x-auto pb-1 lg:flex-col lg:overflow-x-visible lg:pb-0 lg:pr-2"
                    aria-label="Platform settings categories"
                >
                    {CATEGORY_ORDER.map((category) => {
                        const cfg = CATEGORY_CONFIG[category];
                        const Icon = cfg.icon;
                        const isActive = selectedCategory === category;
                        return (
                            <button
                                key={category}
                                type="button"
                                onClick={() => handleSelectCategory(category)}
                                className={cx(
                                    "flex shrink-0 items-center gap-2 rounded-lg px-3 py-2.5 text-left text-sm font-semibold outline-focus-ring transition-colors",
                                    "min-h-11 lg:min-h-0 lg:border-l-2",
                                    isActive
                                        ? "bg-primary_hover text-brand-secondary lg:border-fg-brand-primary_alt"
                                        : "text-tertiary hover:bg-primary_hover hover:text-primary lg:border-transparent"
                                )}
                                aria-current={isActive ? "page" : undefined}
                            >
                                <Icon className="size-4 shrink-0 text-fg-quaternary" aria-hidden />
                                <span className="truncate">{cfg.displayName}</span>
                            </button>
                        );
                    })}
                </nav>

                <div className="min-w-0">
                    {CATEGORY_ORDER.map((category) => {
                        const settings = categorizedSettings[category] || [];
                        return (
                            <CategorySettingsPanel
                                key={category}
                                category={category}
                                settings={settings}
                                isVisible={selectedCategory === category}
                                onSave={(values) => handleSaveCategory(category, values)}
                                onReset={handleResetCategory}
                                isSaving={savingCategory === category}
                            />
                        );
                    })}
                </div>
            </div>

            {Object.keys(categorizedSettings).length === 0 && (
                <div className="rounded-lg border border-secondary bg-secondary/50 p-8 text-center">
                    <p className="text-tertiary">No settings available</p>
                    <p className="text-sm text-tertiary mt-1">Settings will appear here once they are configured.</p>
                </div>
            )}
        </div>
    );
}
